import { Utils as utils } from '@client/utility';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { useCharacterStore } from '@client/store';
import {
  CharacterPayload,
  CharacterPayloadSchema,
  createCharacter,
  createDraft,
  updateCharacter,
  CreateNewChat,
} from '@client/components';

interface CreateCharacterResponse {
  char_uuid?: string;
  error?: string;
  is_moderated?: boolean;
  moderated_message?: string;
  issues?: string[];
}

export const handleDeleteCharacter = async (
  character: CharacterPayload,
  setCharacter: (c: Partial<CharacterPayload>) => void,
  showAlert: (msg: string, type: string) => void
) => {
  if (!character.char_uuid) {
    setCharacter({ ...CharacterPayloadSchema.parse({}) });
    showAlert('Character data cleared.', 'success');
    return;
  }

  try {
    await utils.db.remove({
      tables: 'characters',
      match: { char_uuid: character.char_uuid },
    });
    setCharacter({ ...CharacterPayloadSchema.parse({}) });
    showAlert('Character deleted successfully.', 'success');
  } catch (error: any) {
    Sentry.captureException(error);
    showAlert(error.message || 'Error deleting character.', 'alert');
  }
};

export const handleSubmitCharacter = async (
  character: CharacterPayload,
  type: 'Create' | 'Update' | 'Draft',
  userUuid: string,
  creator?: string,
  showAlert?: (msg: string, type: string) => void,
  navigate?: (url: string) => void
) => {
  const setError = useCharacterStore.getState().setError;

  if (!userUuid) {
    const msg = 'User UUID is missing.';
    showAlert?.(msg, 'alert');
    setError(msg);
    return;
  }

  const char_uuid = character.char_uuid || uuidv4();
  const characterWithUUID: CharacterPayload = {
    ...character,
    char_uuid,
    creator: creator || character.creator || '',
    creator_uuid: userUuid,
  };

  try {
    let response: CreateCharacterResponse;

    if (type === 'Draft') {
      response = await createDraft(characterWithUUID, userUuid);
    } else if (type === 'Create') {
      response = await createCharacter(characterWithUUID);
    } else {
      response = await updateCharacter(characterWithUUID);
    }

    if (response.error) {
      showAlert?.(response.error, 'alert');
      setError(response.error);
      return;
    }

    if (response.is_moderated) {
      const msg =
        response.moderated_message ||
        '⚠️ Your character triggered moderation filters. Please revise and try again.';
      showAlert?.(msg, 'alert');
      setError(msg);
      return;
    }

    if (!response.char_uuid && type !== 'Draft') {
      const msg = 'Character save failed. No UUID returned.';
      showAlert?.(msg, 'alert');
      setError(msg);
      return;
    }

    if (type === 'Draft') {
      showAlert?.('Draft saved successfully!', 'success');
      setError(null);
      return;
    }

    if (type === 'Create' || type === 'Update') {
      const chatResponse = await CreateNewChat(response.char_uuid!, userUuid);
      if (chatResponse.error) {
        Sentry.captureException(new Error(chatResponse.error));
        showAlert?.('Error creating chat.', 'alert');
        setError(chatResponse.error);
        return;
      }
      if (chatResponse.chat_uuid) {
        navigate?.(`/chat/${chatResponse.chat_uuid}`);
        showAlert?.('Chat created successfully.', 'success');
      }
      setError(null);
    }
  } catch (error: any) {
    const msg = error?.message || 'An unexpected error occurred.';
    Sentry.captureException(error);
    showAlert?.(msg, 'alert');
    setError(msg);
  }
};
